
import axiosInstance from "./axiosinstance";

export interface AuthResponse {
  token: string;
  refreshToken: string;
  userId: string;
  email: string;
}

export interface Signin {
  email: string;
  password: string;
}

export interface Register {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  userRole: string;
  phone: string;
}

// export interface verifyEmail {
//   token: string;
// }

// export interface FrogotPassword {
//   emailAddress: string;
// }

export interface NewPassword {
  newPassword: string;
  oldPassword: string;
  confirmPassword: string;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: number;
  isActive: boolean;
  createdOn: string;
  updatedOn: string;
  refreshToken?: string | null;
  refreshTokenExpiryTime?: string | null;
  designation?: string | null;
  signature?: string | null;
  profilePicture?: string | null;
  profilePictureUrl?: string | null;
}

interface ApiResponse<T> {
  data: T;
  totalCount?: number;
}


const dataUrlToFile = (dataUrl: string, fileName: string): File | null => {
  if (!dataUrl || !dataUrl.startsWith("data:")) return null;
  try {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) return null;

    const binaryString = atob(arr[1]);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new File([bytes], fileName, { type: mimeMatch[1] });
  } catch (error) {
    console.error("Unable to convert signature:", error);
    return null;
  }
};

export const authRepository = {
  Login: async (formData: Signin): Promise<AuthResponse> => {
    const { data } = await axiosInstance.post<AuthResponse>(
      "/api/Auth/login",
      formData
    );

    localStorage.setItem("jwt", data.token);
    localStorage.setItem("refreshToken", data.refreshToken);
    if (data.userId) {
      localStorage.setItem("userId", data.userId);
    }

    return data;
  },

  Register: async (data: Register) => {
    const response = await axiosInstance.post("/api/auth/register", data);
    return response.data;
  },

  logout: () => {
    localStorage.clear();
  },

  forgotPassword: async (email: string): Promise<void> => {
    await axiosInstance.post("/api/Auth/forgot-password", { email });
  },

  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    await axiosInstance.post("/api/Auth/reset-password", { token, newPassword });
  },

  RefreshToken: async (): Promise<string | null> => {
    const token = localStorage.getItem("jwt");
    const refreshToken = localStorage.getItem("refreshToken");

    if (!token || !refreshToken) return null;

    try {
      const { data } = await axiosInstance.post<{
        token: string;
        refreshToken: string;
      }>("/api/Auth/refresh", { token, refreshToken });

      const { token: newToken, refreshToken: newRefreshToken } = data;

      localStorage.setItem("jwt", newToken);
      localStorage.setItem("refreshToken", newRefreshToken);

      axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;

      return newToken;
    } catch (error) {
      authRepository.logout();
      return null;
    }
  },

  getUserById: async (id: string): Promise<User> => {
    try {
      const { data } = await axiosInstance.get<User>(`/api/users/${id}`);
      return data;
    } catch (error: any) {
      console.error("Error fetching user by ID:", error);
      if (error.response) {
        throw new Error(error.response.data?.message || "Failed to fetch user.");
      }
      if (error.request) {
        throw new Error("No response from server. Please try again.");
      }
      throw new Error("Something went wrong while fetching user.");
    }
  },

  getUsers: async (): Promise<User[]> => {
    try {
      const response = await axiosInstance.get<ApiResponse<User[]>>("/api/users");
      return Array.isArray(response.data.data) ? response.data.data : [];
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || "Failed to fetch users.");
      }
      throw new Error("Something went wrong while fetching users.");
    }
  },

  //  changePassword: async (payload: NewPassword): Promise<void> => {
  //     await axiosInstance.post("/api/auth/change-password", {
  //       email: payload.email,
  //       oldPassword: payload.oldPassword,
  //       newPassword: payload.newPassword,
  //     });
  //   },

  updateUser: async (
    userId: string,
    updatedData: Partial<User>,
    profilePictureFile?: File
  ): Promise<User> => {
    try {
      const formData = new FormData();
      formData.append("Id", userId);
      formData.append("FirstName", updatedData.firstName ?? "");
      formData.append("LastName", updatedData.lastName ?? "");
      formData.append("Email", updatedData.email ?? "");
      formData.append("Phone", updatedData.phone ?? "");
      if (updatedData.role !== undefined) {
        formData.append("Role", String(updatedData.role));
      }
      if (updatedData.isActive !== undefined) {
        formData.append("IsActive", String(updatedData.isActive));
      }
      if (updatedData.designation) {
        formData.append("Designation", updatedData.designation);
      }
      if (updatedData.profilePicture !== undefined) {
        formData.append("ProfilePicture", updatedData.profilePicture ?? "");
      }
      if (profilePictureFile) {
        formData.append("ProfilePictureFile", profilePictureFile);
      }

      const { data } = await axiosInstance.patch<User>(
        `/api/Users/${userId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data;
    } catch (error: any) {
      console.error("Error updating user:", error);
      if (error.response) {
        const validationErrors = error.response.data?.errors;
        const detail = validationErrors
          ? Object.values(validationErrors).flat().join(" ")
          : error.response.data?.message ||
            (typeof error.response.data === "string" ? error.response.data : "");
        throw new Error(detail || "Failed to update user.");
      }
      throw new Error("Something went wrong while updating user.");
    }
  },

 
  saveESignature: async (
    userId: string,
    signatureDataUrl: string,
    knownUser?: User | null,
    signatureFile?: File
  ): Promise<User> => {
    try {
      const current = knownUser ?? (await authRepository.getUserById(userId));

      const formData = new FormData();
      formData.append("Id", userId);

      formData.append("FirstName", current.firstName ?? "");
      formData.append("LastName", current.lastName ?? "");
      formData.append("Email", current.email ?? "");
      formData.append("Phone", current.phone ?? "");

      // Keep the other fields as they are
      if (current.role !== undefined && current.role !== null) {
        formData.append("Role", String(current.role));
      }
      if (current.isActive !== undefined && current.isActive !== null) {
        formData.append("IsActive", String(current.isActive));
      }
      if (current.designation) {
        formData.append("Designation", current.designation);
      }

      
      formData.append("Signature", signatureDataUrl);
      const file = signatureFile ?? dataUrlToFile(signatureDataUrl, "signature.png");
      if (file) {
        formData.append("SignatureFile", file);
      }

      const { data } = await axiosInstance.patch<User>(
        `/api/users/${userId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data;
    } catch (error: any) {
      console.error("Error saving signature:", error);
      if (error.response) {
        const errs = error.response.data?.errors;
        const detail = errs
          ? Object.values(errs).flat().join(" ")
          : error.response.data?.message ||
            (typeof error.response.data === "string" ? error.response.data : "");
        throw new Error(detail || "Failed to save signature.");
      }
      throw new Error(error?.message || "Something went wrong while saving signature.");
    }
  },

  downloadFile: async (fileKey: string): Promise<Blob> => {
    const response = await axiosInstance.get("/api/Users/download", {
      params: { fileKey },
      responseType: "blob",
    });
    return response.data as Blob;
  },

  resolveProfilePicture: async (user: User): Promise<string | null> => {
    const picture = user.profilePictureUrl || user.profilePicture;
    if (!picture || picture === "null" || picture === "undefined") return null;
    if (
      picture.startsWith("data:") ||
      picture.startsWith("http://") ||
      picture.startsWith("https://")
    ) {
      return picture;
    }

    const blob = await authRepository.downloadFile(picture);
    return URL.createObjectURL(blob);
  },
};

export default authRepository;